import React, { FormEvent, useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { AlertCircle, AtSign, Lock, Moon, SunMedium } from 'lucide-react';
import { signInWithEmail } from '../firebase';
import { joinWaitlist } from '../services/waitlistService';

interface LoginProps {
  onGuest: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

const Login: React.FC<LoginProps> = ({ onGuest, theme, onToggleTheme }) => {
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [waitlistStatus, setWaitlistStatus] = useState<string | null>(null);
  const [isWaitlistSubmitting, setIsWaitlistSubmitting] = useState(false);
  const isDark = theme === 'dark';

  const handleEmailLogin = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoggingIn(true);
    setError(null);
    try {
      await signInWithEmail(email, password);
      // The auth listener in App.tsx will handle the state update automatically
    } catch (err) {
      console.error("Login failed", err);
      const firebaseError = err as FirebaseError;
      const message = firebaseError?.code
        ? `${firebaseError.code.replace('auth/', '').replace(/-/g, ' ')}. Check authorized domains and credentials.`
        : "Failed to sign in. Please check your configuration.";
      setError(message);
      setIsLoggingIn(false);
    }
  };

  const handleWaitlistSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setWaitlistError(null);
    setWaitlistStatus(null);
    setIsWaitlistSubmitting(true);
    try {
      const trimmedEmail = waitlistEmail.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        throw new Error('Please enter a valid email address.');
      }

      // TODO: re-enable spam protection before public launch.
      await joinWaitlist(trimmedEmail);

      setWaitlistStatus("You're on the list — we'll be in touch soon.");
      setWaitlistEmail('');
    } catch (err) {
      setWaitlistError(err instanceof Error ? err.message : 'We couldn’t join the waitlist right now. Please try again soon.');
    } finally {
      setIsWaitlistSubmitting(false);
    }
  };

  const pageClasses = isDark
    ? 'bg-[#071824] text-white'
    : 'bg-sky-50 text-slate-950';
  const toggleClasses = isDark
    ? 'bg-slate-900/80 hover:bg-slate-800 text-slate-200 border-sky-500/20'
    : 'bg-white/90 hover:bg-sky-50 text-slate-700 border-sky-200 shadow-sm';
  const panelClasses = isDark
    ? 'bg-slate-950/80 border-sky-500/20 text-white shadow-sky-950/40'
    : 'bg-white/95 border-sky-100 text-slate-950 shadow-sky-200/50';
  const mutedTextClasses = isDark ? 'text-slate-300' : 'text-slate-600';
  const labelClasses = isDark ? 'text-slate-300' : 'text-slate-700';
  const inputWrapClasses = isDark
    ? 'bg-slate-950/80 border-sky-500/20 focus-within:border-sky-600 focus-within:bg-slate-900'
    : 'bg-white border-slate-200 focus-within:border-sky-600 focus-within:bg-sky-50/60';
  const inputClasses = isDark
    ? 'bg-transparent text-white placeholder:text-slate-500 outline-none w-full'
    : 'bg-transparent text-slate-950 placeholder:text-slate-400 outline-none w-full';
  const waitlistCardClasses = isDark
    ? 'border-sky-500/20 bg-slate-950/70 text-slate-200'
    : 'border-slate-200 bg-white text-slate-800 shadow-sm';
  const helperTextClasses = isDark ? 'text-slate-400' : 'text-slate-600';
  const legalTextClasses = isDark ? 'text-slate-500' : 'text-slate-600';

  return (
    <div className={`flex min-h-screen ${pageClasses} items-start justify-center relative overflow-y-auto font-sans px-4 py-10 md:items-center`}>
      <div className="absolute top-6 right-6 z-20 flex items-center">
        <button
          onClick={onToggleTheme}
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${toggleClasses}`}
        >
          {theme === 'dark' ? (
            <SunMedium size={16} className="text-amber-400" />
          ) : (
            <Moon size={16} className="text-slate-600" />
          )}
          <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
        </button>
      </div>
      {/* Ambient Background Effects */}
      <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-sky-600/25 rounded-full blur-[120px] pointer-events-none opacity-60 animate-pulse" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-cyan-500/15 rounded-full blur-[120px] pointer-events-none opacity-60" />

      <div className={`backdrop-blur-xl border p-8 md:p-12 rounded-3xl shadow-2xl max-w-md w-full z-10 relative flex flex-col items-center ${panelClasses}`}>
        {/* Logo */}
        <div className="mb-8 flex items-center gap-3">
          <img
            src="/logo.jpg?v=2"
            alt="FIRErify logo"
            className="h-10 w-10 rounded-xl object-cover shadow-sm"
          />
          <h1 className={`text-4xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-950'}`}>FIRErify</h1>
        </div>

        <div className="text-center mb-10">
          <p className={`${mutedTextClasses} text-lg`}>
            A modern, AI-powered personal finance dashboard built for long-term wealth, early retirement, and intelligent
            investing.
          </p>
        </div>

        {error && (
          <div className="w-full mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center space-x-2 text-rose-500 text-sm">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form className="space-y-4 w-full" onSubmit={handleEmailLogin}>
          <div className="space-y-2">
            <label className={`text-sm ${labelClasses}`}>Email</label>
            <div className={`flex items-center border rounded-xl px-4 py-3 space-x-3 transition-all ${inputWrapClasses}`}>
              <AtSign size={16} className="text-sky-600" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={inputClasses}
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className={`text-sm ${labelClasses}`}>Password</label>
            <div className={`flex items-center border rounded-xl px-4 py-3 space-x-3 transition-all ${inputWrapClasses}`}>
              <Lock size={16} className="text-sky-600" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={inputClasses}
                placeholder="Enter your password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoggingIn}
            className="w-full bg-sky-600 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center space-x-3 hover:bg-sky-700 transition-all duration-200 transform active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed group shadow-lg shadow-sky-600/20"
          >
            {isLoggingIn ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <span className="transition-colors">Sign in</span>
            )}
          </button>
        </form>

        <div className={`mt-6 w-full rounded-2xl border p-5 text-sm ${waitlistCardClasses}`}>
          <div className="mb-4">
            <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-950'}`}>Join the waitlist</h2>
            <p className={`mt-1 text-xs ${helperTextClasses}`}>
              We&apos;ll notify you when guest access returns. Complete the form below to reserve your spot.
            </p>
          </div>

          {waitlistError && (
            <div className="mb-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-500">
              {waitlistError}
            </div>
          )}
          {waitlistStatus && (
            <div className={`mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs ${isDark ? 'text-emerald-200' : 'text-emerald-700'}`}>
              {waitlistStatus}
            </div>
          )}

          <form className="space-y-3" onSubmit={handleWaitlistSubmit}>
            <div className="space-y-2">
              <label className={`text-xs ${labelClasses}`}>Email</label>
              <div className={`flex items-center border rounded-xl px-4 py-3 space-x-3 transition-all ${inputWrapClasses}`}>
                <AtSign size={14} className="text-sky-600" />
                <input
                  type="email"
                  value={waitlistEmail}
                  onChange={(event) => setWaitlistEmail(event.target.value)}
                  required
                  className={`${inputClasses} text-sm`}
                  placeholder="waitlist@example.com"
                />
              </div>
            </div>


            <button
              type="submit"
              disabled={isWaitlistSubmitting}
              className="w-full rounded-xl border border-sky-600 bg-sky-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60 shadow-sm"
            >
              {isWaitlistSubmitting ? 'Submitting…' : 'Join waitlist'}
            </button>
          </form>
        </div>

        <div className="mt-10 text-center">
          <p className={`text-xs leading-relaxed ${legalTextClasses}`}>
            By accessing this platform, you acknowledge that the analysis provided is for informational purposes only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
